import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { ComponentManager, generateId } from '@adp-wfn/mdf-core';

const isChildless = (item, childField) => {
  return !item[childField] || item[childField].length === 0;
};

const useExpandCollapse = (initialValue = false): [boolean, () => void] => {
  const [isExpanded, setIsExpanded] = useState(initialValue);
  const toggleExpanded = () => setIsExpanded(!isExpanded);

  return [isExpanded, toggleExpanded];
};

const renderContent = (ContentComponent, props) => {
  return (
    <div className={'mdf-tree-list__tree-content'}>
      <div className={`level${props.level}`} />
      <ContentComponent {...props} />
    </div>
  );
};

const TreeItem = (props: any) => {
  const { childField, contentView, item, itemIdField, itemProps, itemView, key, keyField, level } = props;
  const [isExpanded, toggleExpanded] = useExpandCollapse(item.isExpanded);
  const itemRef = useRef<HTMLDivElement>(null);
  const [announcement, setAnnouncement] = useState('');

  const hasChildren = !isChildless(item, childField);
  const id = useMemo(() => item[itemIdField] || generateId('TreeItem'), [item[itemIdField]]);
  const iconClass = isChildless(item, childField) ? '' : (isExpanded ? 'fa fa-minus-circle' : 'fa fa-plus-circle');

  // Tell the application that the user clicked on the tree item, which changed the value of isExpanded.
  const onTreeItemClick = () => {
    const newExpandedState = !isExpanded;
    toggleExpanded();
    props.onExpand?.(item, newExpandedState);

    // Announce the state change for all tree levels
    const itemComponentElement = itemRef.current?.querySelector('.mdf-tree-list__tree-item > *:last-child');
    const itemLabel = itemComponentElement?.textContent?.trim() || item.label || item.name || 'Item';
    const message = `${itemLabel} ${newExpandedState ? 'expanded' : 'collapsed'}`;

    // Force interruption by using temporary placeholder to ensure change detection
    setAnnouncement(' ');
    setTimeout(() => {
      setAnnouncement(message);
    }, 1);
  };

  // Handle keyboard navigation
  const onKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (hasChildren) {
          onTreeItemClick();
        }
        break;
      case 'ArrowRight':
        if (hasChildren && !isExpanded) {
          event.preventDefault();
          onTreeItemClick();
        }
        break;
      case 'ArrowLeft':
        if (hasChildren && isExpanded) {
          event.preventDefault();
          onTreeItemClick();
        }
        break;
      case 'Home':
        event.preventDefault();
        props.onNavigate?.('first');
        break;
      case 'End':
        event.preventDefault();
        props.onNavigate?.('last');
        break;
    }
  };

  // Update isExpanded state on application modifies tree item isExpanded property through external action.
  useEffect(() => {
    if ((item.isExpanded || false) !== isExpanded) {
      toggleExpanded();
    }
  }, [item.isExpanded]);

  const ItemComponent = useMemo(() => ComponentManager.getComponent(itemView), [itemView]);
  const ContentComponent = useMemo(() => ComponentManager.getComponent(contentView), [contentView]);

  return (
    <Fragment>
      <div
        id={id}
        key={key}
        ref={itemRef}
        className={'mdf-tree-list__tree-item'}
        onClick={onTreeItemClick}
        onKeyDown={onKeyDown}
        role="treeitem"
        aria-level={level + 1}
        aria-setsize={props.siblingCount}
        aria-posinset={props.position + 1}
        aria-label={item.label || item.name || `Tree item at level ${level + 1}`}
        aria-describedby={item.description ? `${id}_desc` : undefined}
        tabIndex={0}
      >
        <div className={`level${level}`} />
        <div className={'mdf-tree-list__expand-icon'}>
          <i
            id={`${id}_expandIcon`}
            className={iconClass}
            aria-hidden="true"
            role="presentation"
          />
        </div>
        {item.description && (
          <div id={`${id}_desc`} className="sr-only">
            {item.description}
          </div>
        )}
        <div aria-live="assertive" aria-atomic="true" className="sr-only">
          {announcement}
        </div>
        {ItemComponent && <ItemComponent item={item} {...itemProps} />}
      </div>
      {
        isExpanded && hasChildren && (
          <div role="group" aria-labelledby={id}>
            {item[childField].map((child, index) => renderTreeItem({
              ...props,
              key: item[keyField] || `${id}_${index}`,
              item: child,
              level: level + 1,
              siblingCount: item[childField].length,
              position: index
            }))}
          </div>
        )
      }
      {
        isExpanded && !hasChildren && ContentComponent && renderContent(ContentComponent, props)
      }
    </Fragment>
  );
};

const renderTreeItem = (itemProps) => {
  return <TreeItem {...itemProps} />;
};

export interface ITreeListProps {
  // The name of the field in the item that contains the item's children array. Defaults to 'children'.
  childField?: string;
  // A CSS classname to put on the top-level div that wraps the entire component.
  className?: string;
  // The string name of the view to render in a content area under the item when a 'leaf' node is 'expanded' (clicked)
  contentView?: string;
  // The id for this component. If not provided, a random id is generated.
  id?: string;
  // The name of the field in the item that is the item's id. Defaults to 'id'.
  itemIdField?: string;
  // Additional properties that are passed to the itemView component when rendering the item.
  itemProps?: any;
  // The array of items to render in the TreeList. Each item as passed to the itemView as the item property. This property is required.
  items: any[];
  // The string name of the view or component used to render each item in the tree. This property is required.
  itemView: string;
  // The name of the field to use as the React key for this item. This helps React re-render lists more efficiently. Defaults to the id field with the loop index value appended.
  keyField?: string;
  // The event handler called when a tree item is clicked (triggers expand/collapse)
  onExpand?: (item, isExpanded) => void;
  // The event handler called for keyboard navigation (Home/End keys)
  onNavigate?: (direction: 'first' | 'last') => void;
}

export const TreeList = ({
  childField = 'children',
  className,
  contentView,
  id: originalId,
  itemIdField = 'id',
  itemProps = {},
  itemView,
  items,
  keyField,
  onExpand,
  onNavigate
}: ITreeListProps) => {
  const id = useMemo(() => originalId || generateId('TreeList'), [originalId]);
  const treeListClassName = useMemo(() => 'mdf-tree-list ' + (className || ''), [className]);

  return (
    <div
      id={id}
      className={treeListClassName}
      role="tree"
      aria-label="Tree navigation"
    >
      {
        items?.map((item, index) => renderTreeItem({
          childField,
          contentView,
          key: item[keyField] || `${id}_${index}`,
          keyField,
          item,
          itemIdField,
          itemProps,
          itemView,
          level: 0,
          onExpand,
          onNavigate,
          siblingCount: items.length,
          position: index
        }))
      }
    </div>
  );
};

TreeList.displayName = 'TreeList';
